"use client";

import { MAX_AI_CHAT_FILE_COUNT, MAX_AI_CHAT_TOTAL_FILE_SIZE } from "../constants";
import { AIChatInputFileContext, type AIChatInputFileContextValue, useAIChatInputContext } from "../contexts";
import { useCallback, useMemo, useState } from "react";
import { cx } from "@/lib/tailwind-utils";
import { processFiles } from "./process-files";
interface AIChatInputDropZoneProps {
  children: React.ReactNode;
  accept?: string[];
  maxSize?: number;
  maxFiles?: number;
  maxTotalSize?: number;
  className?: string;
}

/**
 * Provides file-handling context (accept, maxSize, handleFiles) for file-aware
 * compound children like AIChat.Input.FileUpload. Also turns its rendered area
 * into a drop and paste target so users can attach files anywhere inside it.
 */
export function AIChatInputDropZone({
  children,
  accept,
  maxSize = 10 * 1024 * 1024,
  maxFiles = MAX_AI_CHAT_FILE_COUNT,
  maxTotalSize = MAX_AI_CHAT_TOTAL_FILE_SIZE,
  className
}: AIChatInputDropZoneProps) {
  const {
    state,
    actions
  } = useAIChatInputContext();
  if (!actions.addFiles) {
    throw new Error("AIChat.Input.DropZone requires actions.addFiles in AIChatInputContext.");
  }
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const handleFiles = useCallback((files: File[]) => {
    const valid = processFiles(files, {
      accept,
      maxSize,
      maxFiles,
      maxTotalSize,
      existingFiles: state.files
    });
    if (valid) actions.addFiles?.(valid);
  }, [accept, maxSize, maxFiles, maxTotalSize, state.files, actions.addFiles]);
  const fileContextValue = useMemo<AIChatInputFileContextValue>(() => ({
    accept,
    maxSize,
    handleFiles
  }), [accept, maxSize, handleFiles]);

  // Run in capture phase so file pastes don't also insert editor content.
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const files_0 = Array.from(e.clipboardData.files);
    if (files_0.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    handleFiles(files_0);
  }, [handleFiles]);
  const handleDragEnter = useCallback((e_0: React.DragEvent<HTMLDivElement>) => {
    if (!e_0.dataTransfer.types.includes("Files")) return;
    e_0.preventDefault();
    e_0.stopPropagation();
    setIsDraggingOver(true);
  }, []);
  const handleDragOver = useCallback((e_1: React.DragEvent<HTMLDivElement>) => {
    if (!e_1.dataTransfer.types.includes("Files")) return;
    e_1.preventDefault();
    e_1.stopPropagation();
    e_1.dataTransfer.dropEffect = "copy";
  }, []);
  const handleDragLeave = useCallback((e_2: React.DragEvent<HTMLDivElement>) => {
    // Ignore leaves into descendants — only clear when leaving the container.
    if (e_2.relatedTarget instanceof Node && e_2.currentTarget.contains(e_2.relatedTarget)) {
      return;
    }
    e_2.preventDefault();
    e_2.stopPropagation();
    setIsDraggingOver(false);
  }, []);
  const handleDrop = useCallback((e_3: React.DragEvent<HTMLDivElement>) => {
    e_3.preventDefault();
    e_3.stopPropagation();
    setIsDraggingOver(false);
    handleFiles(Array.from(e_3.dataTransfer.files));
  }, [handleFiles]);
  return <AIChatInputFileContext value={fileContextValue}>
      <div className={cx("relative rounded-2xl transition-shadow", isDraggingOver && "ring-2 ring-primary ring-offset-2", className)} onPasteCapture={handlePaste} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        {children}
      </div>
    </AIChatInputFileContext>;
}